<template>
  <div class="ct-sensorial-shell">
    <div class="ct-form-rows sensorial-grid">
      <div
        v-for="section in sections"
        :key="section.title"
        class="ct-section-group"
      >
        <div class="ct-section-head">
          {{ section.title }}
        </div>
        <div
          v-for="item in section.items"
          :key="item.var"
          class="ct-item-row sensorial-card"
          :class="{
            'has-value': answers[item.var] !== '' && answers[item.var] != null,
            'ct-guide-blink': isItemGuideActive(item.num) && age,
          }"
        >
          <div class="sensorial-card-main">
            <div class="ct-num">{{ item.num }}</div>
            <div class="ct-sensorial-label" :title="item.label">
              {{ item.label }}
            </div>
            <div class="ct-freq-row">
              <button
                v-for="v in freqValues"
                :key="v"
                type="button"
                class="ct-freq"
                :class="{ active: answers[item.var] === v }"
                @mousedown="onItemInteract"
                @click="setAnswer(item, v)"
              >
                {{ v }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  SENSORIAL_PADRES_ITEMS,
} from "../data/sensorialPadresData";
import { SENSORIAL_ESCOLAR_SECTIONS } from "../data/sensorialEscolarData";
import { submitCorrection, createCorrectionPayload } from "@/services/testCorrectionAdapter";

const FREQ_VALUES = [0, 1, 2, 3, 4, 5];

function buildPadresSections(items) {
  const sections = [];
  let current = null;
  items.forEach((row) => {
    const title = row.dimension || (current ? current.title : "Ítems");
    if (!current || current.title !== title) {
      current = { title: title, items: [] };
      sections.push(current);
    }
    current.items.push({
      var: row.var,
      num: row.item,
      label: row.descripcion,
    });
  });
  return sections;
}

function buildEscolarSections(rawSections) {
  return rawSections.map((sec) => ({
    title: sec.title,
    items: sec.items.map((it) => {
      const match = it.label.match(/^Item\s+(\d+)/i);
      return {
        var: it.var,
        num: match ? Number(match[1]) : 0,
        label: it.label.replace(/^Item\s+\d+\s*\([^)]+\):\s*/i, ""),
      };
    }),
  }));
}

function createAnswersFromSections(sections) {
  const answers = {};
  sections.forEach((sec) => {
    sec.items.forEach((it) => {
      answers[it.var] = "";
    });
  });
  return answers;
}

const PADRES_SECTIONS = buildPadresSections(SENSORIAL_PADRES_ITEMS);
const ESCOLAR_SECTIONS = buildEscolarSections(SENSORIAL_ESCOLAR_SECTIONS);

export default {
  name: "SensorialForm",
  inject: {
    corregirTestWorkspace: { default: null },
  },
  props: {
    testValue: { type: Number, required: true },
    age: { type: String, default: "" },
    initialAnswers: { type: Object, default: null },
  },
  data() {
    const isPadres = this.testValue === 16;
    const sections = isPadres ? PADRES_SECTIONS : ESCOLAR_SECTIONS;
    return {
      isPadres: isPadres,
      sections: sections,
      freqValues: FREQ_VALUES,
      answers: createAnswersFromSections(sections),
      corrected: false,
      loading: false,
    };
  },
  created() {
    this.hydrate(this.initialAnswers);
  },
  computed: {
    toolbarLabel() {
      return "Corregir test";
    },
    toolbarDisabled() {
      if (this.loading || this.corrected) return true;
      return !this.age;
    },
    unansweredCount() {
      return Object.keys(this.answers).filter(
        (key) => this.answers[key] === "" || this.answers[key] == null
      ).length;
    },
  },
  watch: {
    testValue() {
      this.reset();
    },
    toolbarLabel: { immediate: true, handler: "emitToolbarMeta" },
    toolbarDisabled: { immediate: true, handler: "emitToolbarMeta" },
    loading: { handler: "emitToolbarMeta" },
    age() {
      this.emitToolbarMeta();
    },
  },
  methods: {
    emitToolbarMeta() {
      this.$emit("toolbar-meta", {
        label: this.toolbarLabel,
        disabled: this.toolbarDisabled,
        loading: this.loading,
      });
    },
    isItemGuideActive(num) {
      const ws = this.corregirTestWorkspace;
      return ws && ws.isFieldGuideActive && ws.isFieldGuideActive(num);
    },
    onItemInteract() {
      const ws = this.corregirTestWorkspace;
      if (ws && ws.dismissFieldGuide) ws.dismissFieldGuide();
    },
    setAnswer(item, value) {
      const current = this.answers[item.var];
      this.$set(this.answers, item.var, current === value ? "" : value);
      this.corrected = false;
    },
    buildPayload() {
      const out = { edad: this.age };
      Object.keys(this.answers).forEach((key) => {
        const val = this.answers[key];
        out[key] = val === "" || val == null ? "" : String(val);
      });
      return out;
    },
    // Inversa de buildPayload. `edad` la restaura el workspace (startEdit), no
    // el form: sin edad TestFormRouter ni siquiera monta este componente.
    hydrate(stored) {
      if (!stored) return;
      Object.keys(this.answers).forEach((key) => {
        const raw = stored[key];
        if (raw == null || raw === "") return;
        const num = Number(raw);
        if (!Number.isNaN(num)) this.$set(this.answers, key, num);
      });
    },
    async submit() {
      if (this.toolbarDisabled) return;
      if (this.unansweredCount > 0) {
        if (this.$toasted) {
          this.$toasted.error(
            `Te faltan ${this.unansweredCount} ítem(s) por responder. Completa las tarjetas sin marcar antes de corregir.`
          );
        }
        return;
      }
      this.loading = true;
      try {
        const data = await submitCorrection(
          createCorrectionPayload({
            testValue: this.testValue,
            answers: this.buildPayload(),
          })
        );
        this.corrected = true;
        this.$emit("corrected", data);
      } catch (err) {
        const msg =
          (err && err.message) ||
          "No se pudo corregir el perfil sensorial. Revisa los datos.";
        if (this.$toasted) this.$toasted.error(msg);
      } finally {
        this.loading = false;
      }
    },
    reset() {
      const isPadres = this.testValue === 16;
      this.isPadres = isPadres;
      this.sections = isPadres ? PADRES_SECTIONS : ESCOLAR_SECTIONS;
      this.answers = createAnswersFromSections(this.sections);
      this.corrected = false;
      this.loading = false;
    },
  },
};
</script>
